import {
  dayKey,
  type CheckInTemplate,
  type Entry,
  type EntryQuery,
  type Tag,
  type Track,
} from '@anytracker/core'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { Select } from '../../components/Field.js'
import { formatDate } from '../../format/dates.js'
import { RangeControl } from '../../range/RangeControl.js'
import { DEFAULT_RANGE, useRange } from '../../range/RangeProvider.js'
import { useServices } from '../../services/ServicesProvider.js'
import { EntryRow } from './EntryRow.js'
import styles from './Timeline.module.css'

/** A flat list of headers and entries, which is what makes virtualising this easy. */
type Row =
  | {
      readonly kind: 'day'
      readonly key: string
      readonly at: Entry['at']
      readonly count: number
    }
  | { readonly kind: 'entry'; readonly key: string; readonly entry: Entry }

/**
 * History, newest first (T-305).
 *
 * Grouped by the **captured** local day, not the reader's and not UTC: an entry
 * recorded at 23:30 in New York belongs to that day even though UTC has already
 * rolled over, and getting this wrong is precisely the corruption ADR-0006
 * exists to prevent.
 *
 * Virtualised from the outset. This list grows without bound by design, and
 * retrofitting virtualisation after the row component has grown measurement
 * logic is materially harder — which is why the ticket specified it up front.
 */
export function TimelineScreen() {
  const { repos, clock } = useServices()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const { selection, resolved, setSelection } = useRange()

  const [entries, setEntries] = useState<Entry[] | null>(null)
  const [tracks, setTracks] = useState<Map<string, Track>>(new Map())
  const [tags, setTags] = useState<Map<string, Tag>>(new Map())
  const [templates, setTemplates] = useState<Map<string, CheckInTemplate>>(new Map())
  const [totalEver, setTotalEver] = useState(0)

  // Filters live in the URL, so a filtered view is bookmarkable and survives a
  // refresh — and so the tag screen can link straight into one (T-306).
  const trackId = search.get('track') ?? ''
  const tagId = search.get('tag') ?? ''
  const templateId = search.get('checkin') ?? ''

  /**
   * "All time" is no range filter at all, rather than the resolved bounds.
   *
   * The two are equivalent once the first and last entry have loaded, but the
   * bounds arrive a tick after the first render — long enough to flash a
   * timeline filtered to today — and anything logged since they were read would
   * fall outside them. The timeline's default is the whole of history, and this
   * keeps it exactly that.
   */
  const wholeHistory = selection.kind === 'preset' && selection.preset === 'allTime'

  const query = useMemo((): EntryQuery => {
    const range = wholeHistory
      ? undefined
      : {
          fromEpochMs: resolved.fromEpochMs,
          // The last millisecond of the end day, not its start — otherwise "to
          // the 5th" silently excludes everything recorded on the 5th.
          toEpochMs: resolved.toEpochMs,
        }
    return {
      ...(range === undefined ? {} : { range }),
      ...(trackId === '' ? {} : { trackId }),
      ...(tagId === '' ? {} : { tagId }),
      ...(templateId === '' ? {} : { templateId }),
    }
  }, [wholeHistory, resolved.fromEpochMs, resolved.toEpochMs, trackId, tagId, templateId])

  const filtered = Object.keys(query).length > 0

  const reload = useCallback(async () => {
    const [found, allTracks, allTags, allTemplates, everything] = await Promise.all([
      repos.entries.find(query),
      // Archived included: a measurement against a retired track still has to
      // render, with the configuration it was recorded under.
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
      repos.templates.list({ includeArchived: true }),
      // Counted, not read. The two empty states need to tell "nothing matches"
      // from "nothing exists", which is a number — and reading ten thousand
      // entries to find out how many there are is work this screen does on
      // every filter change.
      repos.entries.count(),
    ])
    setEntries(found)
    setTracks(new Map(allTracks.map((track) => [track.id, track])))
    setTags(new Map(allTags.map((tag) => [tag.id, tag])))
    setTemplates(new Map(allTemplates.map((template) => [template.id, template])))
    setTotalEver(everything)
  }, [repos, query])

  useEffect(() => {
    void reload()
  }, [reload])

  /** Newest first, flattened to day headers plus entries. */
  const rows = useMemo((): Row[] => {
    if (!entries) {
      return []
    }
    const byDay = new Map<string, Entry[]>()
    for (const entry of entries) {
      const key = dayKey(entry.at)
      const bucket = byDay.get(key)
      if (bucket) {
        bucket.push(entry)
      } else {
        byDay.set(key, [entry])
      }
    }

    const out: Row[] = []
    for (const key of [...byDay.keys()].sort().reverse()) {
      const forDay = byDay.get(key) ?? []
      const first = forDay[0]
      if (!first) {
        continue
      }
      out.push({ kind: 'day', key, at: first.at, count: forDay.length })
      for (const entry of [...forDay].sort((a, b) => b.at.epochMs - a.at.epochMs)) {
        out.push({ kind: 'entry', key: entry.id, entry })
      }
    }
    return out
  }, [entries])

  const scroller = useRef<HTMLDivElement>(null)
  const virtual = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    // A starting guess only; rows measure themselves, which they must because
    // an entry's height depends on how many measurements and tags it carries.
    estimateSize: (index) => (rows[index]?.kind === 'day' ? 40 : 96),
    overscan: 8,
  })

  function setFilter(name: string, value: string) {
    const next = new URLSearchParams(search)
    if (value === '') {
      next.delete(name)
    } else {
      next.set(name, value)
    }
    setSearch(next, { replace: true })
  }

  /**
   * Clears the range as well as the dropdowns.
   *
   * The range is a filter — it is why the list is empty as often as a track or
   * a tag is — and a "Clear filters" button that leaves one applied is a button
   * that appears not to work.
   */
  function clearFilters() {
    setSearch(new URLSearchParams(), { replace: true })
    setSelection(DEFAULT_RANGE)
  }

  const deviceZone = clock.timeZone()

  return (
    <>
      <div className={styles['header']}>
        <h1>Timeline</h1>
      </div>

      <div className={styles['filters']}>
        <div className={styles['filterRow']}>
          <Select
            label="Track"
            value={trackId}
            onChange={(event) => setFilter('track', event.target.value)}
          >
            <option value="">Any track</option>
            {[...tracks.values()].map((track) => (
              <option key={track.id} value={track.id}>
                {track.name}
                {track.archived ? ' (archived)' : ''}
              </option>
            ))}
          </Select>
          <Select
            label="Tag"
            value={tagId}
            onChange={(event) => setFilter('tag', event.target.value)}
          >
            <option value="">Any tag</option>
            {[...tags.values()].map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles['filterRow']}>
          <Select
            label="Check-in"
            value={templateId}
            onChange={(event) => setFilter('checkin', event.target.value)}
          >
            <option value="">Any check-in</option>
            {[...templates.values()].map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </Select>
          <div />
        </div>

        {filtered && <Button onClick={clearFilters}>Clear filters</Button>}
      </div>

      {/*
        The same control the charts use (T-402), so moving between them keeps
        the window. Below the other filters because the range is the one people
        change least often here — the timeline opens on all of history.
      */}
      <RangeControl />

      {entries === null ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className={styles['empty']}>
          {/*
            Two distinct empty states. "Nothing matches" and "nothing exists"
            call for completely different next actions, and showing the wrong
            one sends someone looking for data they never recorded.
          */}
          {totalEver === 0 ? (
            <>
              <p className={styles['emptyTitle']}>Nothing recorded yet</p>
              <p className={styles['emptyBody']}>
                Once you log a check-in it will appear here, newest first.
              </p>
              <Button variant="primary" onClick={() => void navigate('/')}>
                Log something
              </Button>
            </>
          ) : (
            <>
              <p className={styles['emptyTitle']}>Nothing matches these filters</p>
              <p className={styles['emptyBody']}>
                There{' '}
                {totalEver === 1 ? 'is 1 entry' : `are ${String(totalEver)} entries`}{' '}
                recorded — just none in this selection.
              </p>
              <Button variant="primary" onClick={clearFilters}>
                Clear filters
              </Button>
            </>
          )}
        </div>
      ) : (
        <div
          className={styles['scroller']}
          ref={scroller}
          data-testid="timeline-scroller"
        >
          <div
            className={styles['viewport']}
            style={{ height: `${String(virtual.getTotalSize())}px` }}
          >
            {virtual.getVirtualItems().map((item) => {
              const row = rows[item.index]
              if (!row) {
                return null
              }
              return (
                <div
                  key={row.key}
                  className={styles['item']}
                  data-index={item.index}
                  ref={virtual.measureElement}
                  style={{ transform: `translateY(${String(item.start)}px)` }}
                >
                  {row.kind === 'day' ? (
                    <div className={styles['day']}>
                      <span>{formatDate(row.at)}</span>
                      <span className={styles['dayCount']}>
                        {row.count === 1 ? '1 entry' : `${String(row.count)} entries`}
                      </span>
                    </div>
                  ) : (
                    <EntryRow
                      entry={row.entry}
                      tracks={tracks}
                      tags={tags}
                      templates={templates}
                      deviceZone={deviceZone}
                      onOpen={() => void navigate(`/timeline/${row.entry.id}`)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
